import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple, faMicrosoft, faGoogle, faAmazon, faMeta, faShopify, faEtsy } from '@fortawesome/free-brands-svg-icons';

const SocialProof = () => {
  const stats = [
    { label: "Global Users", value: "1M+", icon: "👥" },
    { label: "Photos Enhanced", value: "50M+", icon: "🖼️" },
    { label: "Customer Rating", value: "4.9/5", icon: "⭐" },
    { label: "Global Reach", value: "150+ Countries", icon: "🌏" }
  ];

  const testimonials = [
    {
      id: 1,
      content: "This AI tool is a game-changer for our e-commerce photography. The precision in background removal is outstanding, and batch processing has cut our workflow time in half. Absolutely worth every penny!",
      author: "Michael Anderson",
      role: "Head of E-commerce",
      company: "StyleHub",
      image: "https://randomuser.me/api/portraits/men/3.jpg",
      rating: 5
    },
    {
      id: 2,
      content: "I've tried numerous background removal tools, but this one stands out. The edge detection is remarkably accurate, especially with hair and transparent objects. It's now an essential part of my workflow.",
      author: "Sarah Parker",
      role: "Professional Photographer",
      company: "Creative Lens Studio",
      image: "https://randomuser.me/api/portraits/women/1.jpg",
      rating: 5
    },
    {
      id: 3,
      content: "Our marketing team's productivity has skyrocketed since we started using this tool. No more wrestling with complex photo editing software - just clean, professional results in seconds.",
      author: "David Thompson",
      role: "Marketing Director",
      company: "InnovateTech",
      image: "https://randomuser.me/api/portraits/men/2.jpg",
      rating: 5
    }
  ];

  const brands = [
    { name: "Apple", icon: faApple, color: "#555555" },
    { name: "Microsoft", icon: faMicrosoft, color: "#00A4EF" },
    { name: "Google", icon: faGoogle, color: "#4285F4" },
    { name: "Amazon", icon: faAmazon, color: "#FF9900" },
    { name: "Meta", icon: faMeta, color: "#0668E1" },
    { name: "Shopify", icon: faShopify, color: "#7AB55C" },
    { name: "Etsy", icon: faEtsy, color: "#F1641E" }
  ];

  return (
    <section id="social-proof">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent sm:text-5xl mb-4">
            Trusted by Millions Worldwide
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-600">
            Join the global community of satisfied users who trust our AI-powered solution
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4 mb-20">
          {stats.map((stat) => (
            <div 
              key={stat.label} 
              className="bg-gray-50 p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
            >
              <div className="text-5xl mb-4">{stat.icon}</div>
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="mt-2 text-sm text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4">
            What Our Users Say
          </h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Real experiences from professionals who trust our technology
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-gray-50 p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex items-center mb-6">
                <div className="relative">
                  <img
                    className="h-16 w-16 rounded-full object-cover ring-4 ring-blue-50"
                    src={testimonial.image}
                    alt={testimonial.author}
                  />
                  <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900 text-lg">{testimonial.author}</div>
                  <div className="text-sm text-gray-600 font-medium">{testimonial.role}</div>
                  <div className="text-sm text-blue-600 font-medium">{testimonial.company}</div>
                </div>
              </div>
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-600 leading-relaxed">{testimonial.content}</p>
            </div>
          ))}
        </div>

        {/* Brands */}
        <div className="mt-20 py-16 rounded-3xl bg-gradient-to-b from-gray-50 to-white">
          <p className="text-center text-gray-800 font-semibold text-2xl mb-12">
            Trusted by Industry Leaders
          </p>
          <div className="max-w-5xl mx-auto grid grid-cols-4 gap-8 md:grid-cols-7">
            {brands.map((brand) => (
              <div
                key={brand.name}
                className="group flex flex-col items-center justify-center p-6 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <FontAwesomeIcon
                  icon={brand.icon}
                  className="text-4xl mb-3 transition-all duration-300 group-hover:scale-110"
                  style={{ color: brand.color }}
                />
                <span className="text-gray-600 font-medium text-sm group-hover:text-gray-900 transition-colors duration-300">
                  {brand.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;